import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { isServer } from './isServer'
import { getPageContent } from './api'
import { serverData } from './serverData'

// Suburb guides landing page.
// Content comes from the KPod-managed `suburbs` contents row. The server
// fetches it, stashes the list on serverData and renders this list into the
// HTML; the client reads the #suburbdata island on hydration, and falls back
// to fetching the row on client-side navigation.

const metaTitle =
  'Auckland & Waiheke Suburb Guides | Kellands Real Estate'
const metaDescription =
  "Explore Kellands Real Estate's suburb guides — property types, prices, schools, lifestyle and local insights across Auckland's city fringe and Waiheke Island."

const readIsland = () => {
  if (isServer) return null
  const islandEl = document.getElementById('suburbdata')
  if (islandEl && islandEl.innerHTML.trim()) {
    try {
      const parsed = JSON.parse(islandEl.innerHTML)
      // Index island holds the suburb list; ignore a stale single-record
      // object left by client-side nav from a suburb page.
      if (Array.isArray(parsed)) return parsed
    } catch (e) {
      // fall through to API fetch
    }
  }
  return null
}

const SuburbsIndex = () => {
  const [suburbsList, setSuburbsList] = useState(() =>
    isServer
      ? (Array.isArray(serverData.suburbList) ? serverData.suburbList : null)
      : readIsland()
  )

  // Client-side nav fallback: fetch the contents row → object → values
  useEffect(() => {
    if (suburbsList === null) {
      getPageContent('suburbs', (data) => {
        if (data && data.length && data[0].value) {
          try {
            setSuburbsList(Object.values(JSON.parse(data[0].value)))
          } catch (e) {
            setSuburbsList([])
          }
        } else {
          setSuburbsList([])
        }
      }, () => setSuburbsList([]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const all = suburbsList || []

  // Group by region, preserving insertion order within each group
  const regions = all.reduce((acc, s) => {
    (acc[s.region] = acc[s.region] || []).push(s)
    return acc
  }, {})

  return (
    <div className="main-body">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.kellands.co.nz/suburbs" />
      </Helmet>

      <div className="column suburb-page">
        <h1 className="main-page-header">Suburb guides</h1>
        <p className="suburb-headline">
          Local market insights, property types, schools and lifestyle across the
          suburbs Kellands knows best.
        </p>

        {all.length === 0 ? (
          <p>Suburb guides are on the way — check back soon.</p>
        ) : (
          Object.keys(regions).map(region => (
            <section className="suburb-section" key={region}>
              <h2>{region}</h2>
              <ul className="suburb-list suburb-index-list">
                {regions[region].map(s => (
                  <li key={s.slug}>
                    <Link to={`/suburbs/${s.slug}`}>{s.name}</Link>
                    {s.headline && <span className="suburb-index-blurb"> {s.headline}</span>}
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  )
}

export default SuburbsIndex
